import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, MapPin, Video, Layers } from 'lucide-react';
import { PageHeader, PageLoader } from '@repo/ui';

type Filter = 'all' | 'classes' | 'workshops' | 'upcoming' | 'past';

function isSessionPast(session: any): boolean {
  const { status, scheduledTime, sessionDate, startDate } = session;
  if (status === 'completed' || status === 'cancelled') return true;
  if (status === 'ongoing') return false;

  const ds = sessionDate || scheduledTime?.scheduledStartDate || startDate;
  if (ds) {
    const end = new Date(ds);
    const endTime = scheduledTime?.scheduledEndDate;
    if (endTime) {
      end.setTime(new Date(endTime).getTime() + 15 * 60 * 1000); // buffer 15 mins
    } else {
      end.setHours(end.getHours() + 2, 15, 0, 0); // fallback buffer + 2h 15m
    }
    return end < new Date();
  }
  return false;
}

function getStatusConfig(status: string, isPast: boolean, t: any) {
  if (isPast && (status === 'scheduled' || status === 'postponed')) {
    return { label: t('student.sessions.past'), className: 'bg-muted text-muted-foreground' };
  }
  switch (status) {
    case 'completed':
      return {
        label: t('student.sessions.sessionCompleted', { defaultValue: 'Completed' }),
        className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      };
    case 'ongoing':
      return {
        label: t('student.sessions.sessionOngoing', { defaultValue: 'Ongoing' }),
        className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      };
    case 'cancelled':
      return {
        label: t('student.sessions.sessionCancelled', { defaultValue: 'Cancelled' }),
        className: 'bg-red-500/10 text-red-600 border-red-500/20',
      };
    case 'postponed':
      return {
        label: t('student.sessions.sessionPostponed', { defaultValue: 'Postponed' }),
        className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      };
    case 'scheduled':
      return {
        label: t('student.sessions.statusScheduled', { defaultValue: 'Scheduled' }),
        className: 'bg-violet-500/10 text-violet-600 border-violet-500/20',
      };
    default:
      return { label: status, className: 'bg-muted text-muted-foreground' };
  }
}

const formatSessionFormula = (formula: string | undefined, t: any) => {
  if (!formula) return '';
  if (typeof formula !== 'string') return '';

  if (formula.includes('/')) {
    const parts = formula.split('/');
    if (parts.length === 2) {
      return t('student.sessions.formulaWeekSession', {
        week: parts[0],
        session: parts[1],
        defaultValue: `Week ${parts[0]} • Session ${parts[1]}`,
      });
    }
  }

  // If the formula is just a number (e.g., 1 day/week groups), clarify it
  if (/^\d+$/.test(formula.trim())) {
    return t('student.sessions.formulaSessionSub', {
      session: formula.trim(),
      defaultValue: `Session ${formula.trim()}`,
    });
  }

  const parts = formula.split('-');
  return parts.length > 2 ? parts.slice(-2).join('-') : formula;
};

const SessionsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['student-sessions'],
    queryFn: () => api.get('/student/sessions').then((res) => res.data?.data),
  });

  const availableGroups = useMemo(() => {
    if (!sessions) return [];
    const groupMap = new Map();
    sessions.forEach((s: any) => {
      s.groups?.forEach((g: any) => {
        if (!groupMap.has(g._id)) {
          const parsed = (g.name ?? '').split(' | ');
          const shortName =
            parsed.length > 1 ? `${parsed[0].trim()} | ${parsed[1].trim()}` : g.name;
          groupMap.set(g._id, { id: g._id, name: shortName });
        }
      });
    });
    return Array.from(groupMap.values());
  }, [sessions]);

  const filtered = useMemo(() => {
    if (!sessions) return [];
    let tagged = sessions.map((s: any) => ({ ...s, _isPast: isSessionPast(s) }));

    if (selectedGroupId) {
      tagged = tagged.filter((s: any) => s.groups?.some((g: any) => g._id === selectedGroupId));
    }

    const sortByDateAsc = (a: any, b: any) =>
      new Date(a.scheduledTime?.scheduledStartDate || a.sessionDate || 0).getTime() -
      new Date(b.scheduledTime?.scheduledStartDate || b.sessionDate || 0).getTime();
    const sortByDateDesc = (a: any, b: any) =>
      new Date(b.scheduledTime?.scheduledStartDate || b.sessionDate || 0).getTime() -
      new Date(a.scheduledTime?.scheduledStartDate || a.sessionDate || 0).getTime();

    const getSortedList = (list: any[]) => {
      const past = list.filter((s: any) => s._isPast).sort(sortByDateDesc);
      const upcoming = list.filter((s: any) => !s._isPast).sort(sortByDateAsc);
      return [...upcoming, ...past];
    };

    if (filter === 'past') return tagged.filter((s: any) => s._isPast).sort(sortByDateDesc);
    if (filter === 'upcoming') return tagged.filter((s: any) => !s._isPast).sort(sortByDateAsc);
    if (filter === 'workshops')
      return getSortedList(tagged.filter((s: any) => s.type === 'workshop'));
    if (filter === 'classes') return getSortedList(tagged.filter((s: any) => s.type === 'class'));

    return getSortedList(tagged);
  }, [sessions, filter, selectedGroupId]);

  const filterTabs: { key: Filter; label: string }[] = [
    { key: 'all', label: t('student.sessions.all') },
    { key: 'classes', label: t('student.sessions.classesFilter', { defaultValue: 'Classes' }) },
    {
      key: 'workshops',
      label: t('student.sessions.workshopsFilter', { defaultValue: 'Workshops' }),
    },
    { key: 'upcoming', label: t('student.sessions.upcoming') },
    { key: 'past', label: t('student.sessions.past') },
  ];

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        title={t('student.sessions.title')}
        subtitle={t('student.sessions.subtitle', {
          defaultValue: 'Explore your session history and view upcoming scheduled appointments',
        })}
        icon={Calendar}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      <div className="flex flex-col gap-3 mt-4">
        {/* Main Status/Type Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`cursor-pointer px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors border ${
                filter === tab.key
                  ? 'bg-primary border-primary text-primary-foreground shadow-sm'
                  : 'bg-card border-border hover:bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dynamic Groups Filter (only show if student has more than 1 visible group in sessions) */}
        {availableGroups.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedGroupId(null)}
              className={`cursor-pointer px-3 py-1.5 text-[13px] font-medium rounded-lg whitespace-nowrap transition-colors ${
                selectedGroupId === null
                  ? 'bg-primary/15 text-primary'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {t('student.sessions.allGroups', { defaultValue: 'All Groups' })}
            </button>
            {availableGroups.map((g: any) => (
              <button
                key={g.id}
                onClick={() => setSelectedGroupId(g.id)}
                className={`cursor-pointer px-3 py-1.5 text-[13px] font-medium rounded-lg whitespace-nowrap transition-colors ${
                  selectedGroupId === g.id
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? (
        <PageLoader message={t('common.loading')} className="min-h-64" />
      ) : !filtered.length ? (
        <div className="text-center py-16 text-muted-foreground">
          {t('student.sessions.noSessions')}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((session: any) => {
            const isPast = session._isPast;

            // Actual Date Extracted Match mobile logic
            const actualDate: Date | null = session.sessionDate
              ? new Date(session.sessionDate)
              : session.scheduledTime?.scheduledStartDate
                ? new Date(session.scheduledTime.scheduledStartDate)
                : session.startDate
                  ? new Date(session.startDate)
                  : null;

            const endRaw = session.scheduledTime?.scheduledEndDate;

            const timeLabelStr = actualDate
              ? actualDate.toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                }) +
                (endRaw
                  ? ` – ${new Date(endRaw).toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' })}`
                  : '')
              : null;

            const formulaStr = formatSessionFormula(session.sessionFormula, t);
            const titleFallback =
              session.groups
                ?.map((g: any) => {
                  const parsed = (g.name ?? '').split(' | ');
                  return parsed.length > 1 ? `${parsed[0].trim()} | ${parsed[1].trim()}` : g.name;
                })
                .join(', ') || '—';

            const isTitleFormula = session.type !== 'workshop' && !!formulaStr;
            const title = isTitleFormula ? formulaStr : titleFallback;
            const statusConfig = getStatusConfig(session.status, isPast, t);

            return (
              <div
                key={session._id}
                className={`flex flex-row overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md ${
                  isPast ? 'opacity-70' : ''
                }`}
              >
                <div
                  className={`w-1.5 shrink-0 ${isPast ? 'bg-muted-foreground' : 'bg-primary'}`}
                />

                <div className="flex-1 p-4 sm:p-5 flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-base truncate" title={title}>
                        {title}
                      </h3>

                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {/* Type Badge */}
                        <span
                          className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${
                            session.type === 'workshop'
                              ? 'bg-amber-500/15 text-amber-600'
                              : 'bg-emerald-500/15 text-emerald-600'
                          }`}
                        >
                          {session.type === 'workshop'
                            ? t('student.sessions.workshopBadge', { defaultValue: 'Workshop' })
                            : t('student.sessions.classBadge', { defaultValue: 'Class' })}
                        </span>

                        {/* Exception Badge */}
                        {session.nature === 'exception' && (
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-violet-500/15 text-violet-600">
                            {t('student.sessions.exceptionBadge', { defaultValue: 'Exception' })}
                          </span>
                        )}

                        {/* Host Name */}
                        {session.host?.name && (
                          <span className="text-xs font-medium text-muted-foreground">
                            {session.host.name}
                          </span>
                        )}
                      </div>

                      {/* Session Details */}
                      {(session.sessionNumber != null || isTitleFormula) && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground flex-wrap">
                          {isTitleFormula && (
                            <div className="flex items-center gap-1.5 bg-secondary/50 px-2 py-0.5 rounded-md border border-border/50 transition-colors hover:bg-secondary">
                              <Layers className="h-3 w-3 opacity-70" />
                              <span className="font-medium text-[11px]">{titleFallback}</span>
                            </div>
                          )}

                          {session.sessionNumber != null && (
                            <span className="font-medium bg-muted/40 px-2 py-0.5 rounded-md border border-border/30">
                              {t('student.sessions.sessionNumber', { defaultValue: 'Session #' })}:{' '}
                              {session.sessionNumber}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div
                      className={`px-2.5 py-1 text-xs rounded-lg font-medium border shrink-0 ${statusConfig.className}`}
                    >
                      {statusConfig.label}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-medium text-muted-foreground flex-wrap">
                    {actualDate && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {actualDate.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    )}

                    {timeLabelStr && (
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {timeLabelStr}
                      </span>
                    )}

                    {session.branch && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {locale === 'ar'
                          ? session.branch.nameAr || session.branch.nameEn
                          : session.branch.nameEn}
                      </span>
                    )}
                  </div>

                  {session.deliveryMode && (
                    <div className="flex items-center gap-1.5 self-start px-2.5 py-1 text-[11px] font-medium rounded-md bg-primary/10 text-primary">
                      {session.deliveryMode === 'online' ? (
                        <Video className="h-3 w-3" />
                      ) : (
                        <MapPin className="h-3 w-3" />
                      )}
                      <span>{session.deliveryMode}</span>
                    </div>
                  )}

                  {session.meeting?.joinUrl && !isPast && (
                    <a
                      href={session.meeting.joinUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex w-full items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors"
                    >
                      <Video className="h-4 w-4" />
                      {t('student.sessions.joinNow', { defaultValue: 'Join Now' })}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
